import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, TextField, makeStyles, useTheme } from "@seahorse/ui";
import html2pdf from "html2pdf.js";
import { FaCopy as _FaCopy, FaDownload as _FaDownload, FaPen as _FaPen, FaRedo as _FaRedo } from "react-icons/fa";
import { RenderFromString, SupportingSources } from "../CoTraderChatTileComponent";
import StreamingStatus from "../../../UtilComponent/StreamingStatus";
import { getAuthenticatedWebSocketUrl } from "../../Utils/CoTraderUtils";
import { createBestxSummaryHtmlStyles, mdToHtml } from "./bestxSummaryFormatting";
import { useResponsiveTileTypography } from "../../Utils/useResponsiveTileTypography";

const FaCopy = _FaCopy as unknown as FC<any>;
const FaDownload = _FaDownload as unknown as FC<any>;
const FaPen = _FaPen as unknown as FC<any>;
const FaRedo = _FaRedo as unknown as FC<any>;
const BESTX_REQUEST_TIME_ZONE = "UTC";

interface BestXConversationTileComponentProps {
  currencyPair: string;
  executionTimestamp: string;
  executionSpotRate?: number | string | null;
  executionForwardPoints?: number | string | null;
  expirationTimestamp?: string | null;
  analysisWindow: "1d" | "1w";
  analysisSources: {
    prices: boolean;
    trading_economics: boolean;
    realtime_news: boolean;
    truth_social: boolean;
  };
  userLanguage: string;
}

type ChatMessage = {
  id: string;
  type: "question" | "answer";
  text: string;
  segments?: AnswerContentSegment[];
  backendMessageId?: string;
  sources?: string[];
  variant?: "text" | "summary" | "report";
};

type AnswerContentSegment = {
  format: "markdown" | "html";
  content: string;
};

const createBestXConversationId = () => {
  const userId = localStorage.getItem("userId") || "anonymous";
  const randomSuffix = Math.floor(100000 + Math.random() * 900000);
  return `BESTX-GPT-CONV-${userId}-${Date.now()}-${randomSuffix}`;
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const toPlainText = (htmlString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

// Sources arrive as whitespace-separated URLs; keep them off the HTML stream
// so they can render as LinkPreview chips instead of raw anchor text.
const appendSources = (current: ChatMessage, rawUrls: string): ChatMessage => {
  const urls = rawUrls.split(/\s+/).filter(Boolean);
  if (urls.length === 0) return current;

  const merged = [...(current.sources || [])];
  urls.forEach((url) => {
    if (!merged.includes(url)) merged.push(url);
  });
  return { ...current, sources: merged };
};

const hasHtmlTags = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const getAnswerContentSegments = (message: ChatMessage) => {
  if (message.segments?.length) return message.segments;
  if (!message.text) return [];

  return [
    {
      format: hasHtmlTags(message.text) ? "html" : "markdown",
      content: message.text,
    },
  ] as AnswerContentSegment[];
};

const appendAnswerContent = (
  current: ChatMessage,
  content: string,
  format: AnswerContentSegment["format"]
) => {
  if (!content) return current;

  const segments = getAnswerContentSegments(current);
  const lastSegment = segments[segments.length - 1];
  const nextSegments =
    lastSegment?.format === format
      ? [
          ...segments.slice(0, -1),
          { ...lastSegment, content: `${lastSegment.content}${content}` },
        ]
      : [...segments, { format, content }];

  return {
    ...current,
    text: `${current.text}${content}`,
    segments: nextSegments,
  };
};

const wrapSpecialPayload = (title: string, html: string) =>
  `<section><h3>${escapeHtml(title)}</h3>${html}</section>`;

const appendSpecialPayload = (
  current: ChatMessage,
  variant: "summary" | "report",
  title: string,
  payload: string
) => {
  if (current.variant === variant) {
    return appendAnswerContent(
      {
        ...current,
        variant,
      },
      payload,
      hasHtmlTags(payload) ? "html" : "markdown"
    );
  }

  if (hasHtmlTags(payload)) {
    return appendAnswerContent(
      { ...current, variant },
      wrapSpecialPayload(title, payload),
      "html"
    );
  }

  return appendAnswerContent(
    { ...current, variant },
    `### ${title}\n\n${payload}`,
    "markdown"
  );
};

const normalizeNumber = (value: number | string | null | undefined) => {
  if (value === "" || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const buildSuggestions = () => [
  "Generate a post-trade execution report.",
  "How did the market move before and after this trade?",
  "What were the main market drivers around the time of execution?",
  "Were there any significant news events or economic releases affecting the market?",
];

const BestXConversationTileComponent: React.FunctionComponent<
  BestXConversationTileComponentProps
> = ({
  currencyPair,
  executionTimestamp,
  executionSpotRate,
  executionForwardPoints,
  expirationTimestamp,
  analysisWindow,
  analysisSources,
  userLanguage,
}) => {
  const theme = useTheme();
  const { containerRef, tier } =
    useResponsiveTileTypography<HTMLDivElement>();
  const colors = useMemo(
    () => ({
      dark: theme.globallink.colors.Grey[1100],
      light: theme.globallink.colors.White[500],
      border: theme.globallink.colors.White[500],
      accent: theme.globallink.colors.Blue[500],
      muted: theme.globallink.colors.Grey[200],
      surface: "rgba(255,255,255,0.06)",
      reportText: "#E8EDF4",
    }),
    [theme]
  );

  const typography = useMemo(() => {
    switch (tier) {
      case "compact":
        return {
          bodyPadding: "8px",
          bodyGap: "8px",
          messageSize: "12px",
          messageLineHeight: 1.5,
          messagePadding: "8px 10px",
          progressSize: "11px",
          suggestionSize: "11px",
          suggestionMinHeight: "52px",
        };
      case "comfortable":
        return {
          bodyPadding: "12px",
          bodyGap: "12px",
          messageSize: "14px",
          messageLineHeight: 1.6,
          messagePadding: "12px 14px",
          progressSize: "13px",
          suggestionSize: "13px",
          suggestionMinHeight: "60px",
        };
      default:
        return {
          bodyPadding: "10px",
          bodyGap: "10px",
          messageSize: "13px",
          messageLineHeight: 1.55,
          messagePadding: "10px 12px",
          progressSize: "12px",
          suggestionSize: "12px",
          suggestionMinHeight: "56px",
        };
    }
  }, [tier]);
  const summaryHtmlStyles = useMemo(
    () => createBestxSummaryHtmlStyles(tier),
    [tier]
  );

  const useStyles = makeStyles({
    container: {
      width: "100%",
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      // Matches BestxSummaryTileComponent so "Ask EMAlpha" sits level with
      // "Market Summary" in the side-by-side layout.
      padding: "14px",
      boxSizing: "border-box",
      overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.14)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
    },
    // Header row and title mirror BestxSummaryTileComponent's header. Top
    // alignment keeps the title fixed when "Clear Chat" appears.
    headerRow: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: "12px",
      paddingBottom: "10px",
      flexWrap: "wrap",
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      minWidth: 0,
    },
    title: {
      color: colors.light,
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "18px",
      margin: 0,
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
    },
    clearButton: {
      minWidth: 0,
      // Label shares the title's 18px line box, and the negative margin cancels
      // the button's own top padding, so "Clear Chat" sits level with the title
      // under the header row's flex-start alignment.
      padding: "2px 6px",
      marginTop: "-2px",
      lineHeight: "18px",
      fontSize: "13px",
      textTransform: "none",
      color: "rgba(224,224,224,0.76)",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.08)",
        color: colors.light,
      },
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      height: "auto",
      width: "100%",
      backgroundColor: "transparent",
      padding: "10px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      gap: "10px",
    },
    message: {
      padding: "10px 12px",
      borderRadius: "12px",
      width: "fit-content",
      maxWidth: "100%",
    },
    question: {
      alignSelf: "flex-end",
      marginLeft: "auto",
      maxWidth: "80%",
      backgroundColor: colors.accent,
      color: "white",
    },
    // Answers read as plain text on the tile; only questions get a bubble.
    answer: {
      alignSelf: "flex-start",
      marginRight: "auto",
      width: "100%",
      color: colors.reportText,
      padding: 0,
    },
    messageActions: {
      display: "flex",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: "14px",
      marginTop: "12px",
    },
    actionIcon: {
      cursor: "pointer",
      fontSize: "12px",
      color: "rgba(232,237,244,0.55)",
      transition: "color 0.15s ease",
      "&:hover": {
        color: colors.reportText,
      },
    },
    summaryHtml: {
      ...summaryHtmlStyles,
      color: colors.reportText,
      "& code": {
        background: "rgba(255,255,255,0.08)",
        color: colors.reportText,
      },
    },
    suggestionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: "8px",
      "@media (max-width: 720px)": {
        gridTemplateColumns: "minmax(0, 1fr)",
      },
    },
    suggestionButton: {
      width: "100%",
      textAlign: "left",
      justifyContent: "flex-start",
      lineHeight: 1.35,
      minHeight: "56px",
      padding: "12px 14px",
      border: "1px solid rgba(255,255,255,0.14)",
      borderRadius: "14px",
      backgroundColor: colors.surface,
      color: colors.light,
      boxShadow: "none",
      whiteSpace: "normal",
      transition: "background-color 0.15s ease, border-color 0.15s ease",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.1)",
        borderColor: "rgba(255,255,255,0.22)",
      },
    },
    composer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "10px",
      marginTop: "10px",
      "@media (max-width: 720px)": {
        flexDirection: "column",
        alignItems: "stretch",
      },
    },
    inputWrap: {
      flex: 1,
      minWidth: 0,
    },
    loadingResponse: {
      alignSelf: "flex-start",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: colors.muted,
      lineHeight: 1.5,
    },
  });

  const classes = useStyles();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const chatIdRef = useRef(createBestXConversationId());
  const isFirstTradeStateRef = useRef(true);
  // Once the answer has content, the streaming text is its own progress signal,
  // so the standalone loading line would just sit under it doing nothing.
  const lastMessage = messages[messages.length - 1];
  const answerHasStarted =
    lastMessage?.type === "answer" &&
    Boolean(lastMessage.text || lastMessage.sources?.length);
  // The rendered list is filtered, so map each answer back to the question that
  // produced it for the edit action.
  // Actions belong to a settled answer; the one still streaming keeps them
  // mounted but hidden so nothing shifts when it finishes.
  const isStreamingMessage = (id: string) =>
    loading && id === lastMessage?.id;
  const questionForAnswer = useMemo(() => {
    const byAnswerId = new Map<string, string>();
    let pendingQuestion = "";
    messages.forEach((entry) => {
      if (entry.type === "question") pendingQuestion = entry.text;
      else byAnswerId.set(entry.id, pendingQuestion);
    });
    return byAnswerId;
  }, [messages]);

  const suggestions = useMemo(() => buildSuggestions(), []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(() => {
    return () => {
      try {
        socketRef.current?.close();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (isFirstTradeStateRef.current) {
      isFirstTradeStateRef.current = false;
      return;
    }

    try {
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createBestXConversationId();
    setMessages([]);
    setLoading(false);
  }, [
    currencyPair,
    executionTimestamp,
    executionSpotRate,
    executionForwardPoints,
    expirationTimestamp,
  ]);

  const updateAnswerMessage = (
    messageId: string,
    updater: (message: ChatMessage) => ChatMessage
  ) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? updater(message) : message
      )
    );
  };

  const resetConversation = () => {
    try {
      socketRef.current?.close();
    } catch {}

    socketRef.current = null;
    chatIdRef.current = createBestXConversationId();
    setMessages([]);
    setLoading(false);
    setMessageInput("");
  };

  const copyMessage = async (htmlString: string) => {
    try {
      await navigator.clipboard.writeText(toPlainText(htmlString));
      alert("Copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const downloadMessage = async (htmlString: string) => {
    const container = document.createElement("div");
    container.innerHTML = htmlString;
    container.style.padding = "24px";
    container.style.fontFamily = "Arial, sans-serif";
    container.style.fontSize = "12px";
    document.body.appendChild(container);

    try {
      await html2pdf()
        .set({
          margin: 0.5,
          filename: `bestx-report-${currencyPair.toLowerCase()}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(container)
        .save();
    } catch (error) {
      console.error("PDF export failed:", error);
    } finally {
      document.body.removeChild(container);
    }
  };

  const handleSendMessage = async (rawInput?: string) => {
    if (loading) return;

    const userInput = (rawInput ?? messageInput).trim();
    if (!userInput) return;

    const apiUrl = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("userId") || "anonymous";
    const timeZone = BESTX_REQUEST_TIME_ZONE;

    if (!apiUrl) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          type: "answer",
          text: "<strong>Error:</strong> Chat is temporarily unavailable. Please try again later.",
        },
      ]);
      return;
    }

    const questionId = `question-${Date.now()}`;
    const answerId = `answer-${Date.now()}`;
    const requestBody = {
      analysis_surface: "bestx",
      origin: "BESTX",
      analysis_mode: "chat_v1",
      chat_id: chatIdRef.current,
      bestx_mode: "conversation_v1",
      currency_pair: currencyPair ?? null,
      execution_timestamp: executionTimestamp ?? null,
      execution_timezone: timeZone,
      execution_spot_rate: normalizeNumber(executionSpotRate),
      execution_forward_points: normalizeNumber(executionForwardPoints),
      expiration_timestamp: expirationTimestamp ?? null,
      analysis_window: analysisWindow,
      analysis_sources: analysisSources,
      query: userInput,
      userTimeZone: timeZone,
      user_language: userLanguage,
      embedding_model: "text-embedding-ada-002",
    };

    try {
      socketRef.current?.close();
    } catch {}

    setLoading(true);
    setMessageInput("");
    setMessages((prev) => [
      ...prev,
      { id: questionId, type: "question", text: escapeHtml(userInput) },
      { id: answerId, type: "answer", text: "", variant: "text" },
    ]);

    let ws: WebSocket;
    try {
      const wsUrl = await getAuthenticatedWebSocketUrl(chatIdRef.current, userId);
      ws = new WebSocket(wsUrl);
    } catch (error) {
      console.error("BestX conversation auth error:", error);
      updateAnswerMessage(answerId, (current) =>
        current.text
          ? current
          : appendAnswerContent(
              current,
              "<strong>Error:</strong> Chat is temporarily unavailable. Please try again later.",
              "html"
            )
      );
      setLoading(false);
      return;
    }
    socketRef.current = ws;

    const finishTurn = () => {
      setLoading(false);
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      try {
        ws.close();
      } catch {}
    };

    ws.onopen = () => {
      ws.send(JSON.stringify(requestBody));
    };

    ws.onmessage = (event) => {
      let data: any;

      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const type = data?.type;
      const message = data?.message;

      if (
        type === "FINISH" ||
        type === "DONE" ||
        type === "END" ||
        type === "COMPLETE"
      ) {
        finishTurn();
        return;
      }

      if (type === "MESSAGE_ID" && message != null) {
        updateAnswerMessage(answerId, (current) => ({
          ...current,
          backendMessageId: String(message),
        }));
        return;
      }

      if (type === "INFO") {
        return;
      }

      if (type === "SUMMARY_UPDATE" || type === "BESTX_STATE") {
        return;
      }

      if (message == null) {
        return;
      }

      if (type === "urls") {
        updateAnswerMessage(answerId, (current) =>
          appendSources(current, String(message))
        );
        return;
      }

      if (type === "BESTX_SUMMARY_HTML") {
        updateAnswerMessage(answerId, (current) =>
          appendSpecialPayload(
            current,
            "summary",
            "On-Demand Summary",
            String(message)
          )
        );
        return;
      }

      if (type === "BESTX_REPORT_HTML") {
        updateAnswerMessage(answerId, (current) =>
          appendSpecialPayload(
            current,
            "report",
            "Final Report",
            String(message)
          )
        );
        return;
      }

      if (type === "ERROR") {
        updateAnswerMessage(answerId, (current) =>
          appendAnswerContent(
            current,
            `<div><strong>Error:</strong> ${String(message)}</div>`,
            "html"
          )
        );
        return;
      }

      const content = String(message);
      updateAnswerMessage(answerId, (current) =>
        appendAnswerContent(
          current,
          content,
          hasHtmlTags(content) ? "html" : "markdown"
        )
      );
    };

    ws.onerror = (error) => {
      console.error("BestX conversation WebSocket error:", error);
      updateAnswerMessage(answerId, (current) =>
        current.text
          ? current
          : appendAnswerContent(
              current,
              "<strong>Error:</strong> Chat is temporarily unavailable. Please try again later.",
              "html"
            )
      );
      finishTurn();
    };

    ws.onclose = () => {
      if (socketRef.current === ws) {
        socketRef.current = null;
      }
      setLoading(false);
    };
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSendMessage();
    }
  };
  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.headerRow}>
        <div className={classes.headerLeft}>
          <p className={classes.title}>
            Ask EMAlpha
          </p>
        </div>

        {(loading || messages.length > 0) && (
          <div className={classes.headerActions}>
            <Button
              variant="text"
              onClick={resetConversation}
              className={classes.clearButton}
            >
              <FaRedo className="me-2" />
              Clear Chat
            </Button>
          </div>
        )}
      </div>

      <div
        className={classes.body}
        style={{ padding: typography.bodyPadding, gap: typography.bodyGap }}
      >
        {messages.length === 0 && (
          <div>
            <div className={classes.suggestionGrid}>
              {suggestions.map((suggestion) => (
                <Button
                  key={suggestion}
                  className={classes.suggestionButton as any}
                  onClick={() => handleSendMessage(suggestion)}
                  style={{
                    fontSize: typography.suggestionSize,
                    minHeight: typography.suggestionMinHeight,
                    textTransform: "none",
                  }}
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        )}

        {messages
          .filter(
            (message) =>
              message.type !== "answer" ||
              Boolean(message.text) ||
              Boolean(message.sources?.length)
          )
          .map((message) => (
            <div
              key={message.id}
              className={`${classes.message} ${
                message.type === "question" ? classes.question : classes.answer
              }`}
              style={{
                fontSize: typography.messageSize,
                lineHeight: typography.messageLineHeight,
                // Bubble padding belongs to questions; answers sit flush.
                padding:
                  message.type === "question" ? typography.messagePadding : 0,
              }}
            >
              {message.type === "answer" ? (
                <div
                  className={classes.summaryHtml}
                  style={{
                    fontSize: typography.messageSize,
                    lineHeight: typography.messageLineHeight,
                  }}
                >
                  {getAnswerContentSegments(message).map((segment, index) => (
                    <div
                      key={`${message.id}-${index}-${segment.format}`}
                      dangerouslySetInnerHTML={{
                        __html:
                          segment.format === "html"
                            ? segment.content
                            : mdToHtml(segment.content),
                      }}
                    />
                  ))}
                  {message.sources?.length ? (
                    <SupportingSources urls={message.sources} />
                  ) : null}
                </div>
              ) : (
                <RenderFromString htmlString={message.text} />
              )}

              {message.type === "answer" && message.text && (
                <div
                  className={classes.messageActions}
                  style={{
                    opacity: isStreamingMessage(message.id) ? 0 : 1,
                    pointerEvents: isStreamingMessage(message.id)
                      ? "none"
                      : "auto",
                    transition: "opacity 120ms ease",
                  }}
                >
                  <FaCopy
                    className={classes.actionIcon as any}
                    onClick={() => copyMessage(message.text)}
                    title="Copy Message"
                  />
                  <FaPen
                    className={classes.actionIcon as any}
                    title="Edit Message"
                    onClick={() => {
                      setMessageInput(questionForAnswer.get(message.id) || "");
                      document
                        .getElementById("bestxConversationInput")
                        ?.focus();
                    }}
                  />
                  {message.variant === "report" && (
                    <FaDownload
                      className={classes.actionIcon as any}
                      onClick={() => downloadMessage(message.text)}
                      title="Download Report"
                    />
                  )}
                </div>
              )}
            </div>
          ))}

        {loading && !answerHasStarted && (
          <div
            className={classes.loadingResponse}
            style={{ fontSize: typography.progressSize }}
          >
            <StreamingStatus label="Loading response..." />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={classes.composer}>
        <div className={classes.inputWrap}>
          <TextField
            size="small"
            placeholder="Type your question"
            value={messageInput}
            id="bestxConversationInput"
            onChange={(value) => setMessageInput(value)}
            onKeyDown={handleKeyDown}
            endAdornment={
              <div
                className="hover:text-info"
                onClick={() => handleSendMessage()}
                style={{ cursor: "pointer" }}
              >
                <i className="fa-solid fa-paper-plane me-2"></i>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default BestXConversationTileComponent;
